import _extends from "@babel/runtime/helpers/extends";
import "core-js/modules/es.function.name.js";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { hasReactChildren } from 'utils/ui';
import getAccumulatedCompletionTime from 'utils/asset/getAccumulatedCompletionTime';
import Button from 'components/Button';
import ContentProgressCircle from 'components/ContentProgressCircle';
import Flag from 'components/Flag';
import Flex from 'components/Flex';
import Stack from 'components/Stack';
import { useDisclosure } from 'components/Disclosure';
import Icon from 'components/Icon';
import { getAllContentChildren } from 'screens/PlaylistManageContentScreen/Editor/utils/getAllContentChildren';
import SectionBlock from './SectionBlock';
import SectionContentInfo from './SectionContentInfo';
import VisuallyHidden from 'components/VisuallyHidden';
import { getSectionProgress } from './utils/getSectionProgress';
import { checkIsSectionLockedForPreview } from './utils/checkIsSectionLockedForPreview';
import { useLockingInfoText } from './utils/useLockingInfoText';
function Section(_ref) {
  var isSectionOpen = _ref.isSectionOpen,
    hasPlaylistNumbering = _ref.hasPlaylistNumbering,
    _ref$isPreviewMode = _ref.isPreviewMode,
    isPreviewMode = _ref$isPreviewMode === void 0 ? false : _ref$isPreviewMode,
    section = _ref.section,
    children = _ref.children,
    onToggleOpen = _ref.onToggleOpen;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useDisclosure = useDisclosure({
      controlledState: isSectionOpen,
      setControlledState: onToggleOpen
    }),
    isVisible = _useDisclosure.isVisible,
    buttonProps = _useDisclosure.buttonProps,
    contentProps = _useDisclosure.contentProps;
  var sectionIsLockedForPreview = checkIsSectionLockedForPreview(isPreviewMode, section);
  var showAsLocked = isPreviewMode ? sectionIsLockedForPreview : !!section.isLockedForUser;
  var lockingInfoText = useLockingInfoText({
    section: section
  });
  var sectionContent = getAllContentChildren(section.children);
  var sectionProgress = getSectionProgress(sectionContent);
  var contentItemCount = (isPreviewMode ? sectionContent.length : section.contentCount) || 0;
  var hideItemCount = showAsLocked && contentItemCount === 0;
  var hasSectionContent = contentItemCount > 0;
  var sectionDuration = (isPreviewMode ? getAccumulatedCompletionTime(sectionContent) : section.completionTime) || 0;
  return /*#__PURE__*/React.createElement(Flag, {
    align: "top",
    spacing: "s",
    token: /*#__PURE__*/React.createElement(ContentProgressCircle, {
      contentName: section.name,
      infoReadout: "tooltip",
      progress: sectionProgress,
      isContentLocked: showAsLocked
    })
  }, /*#__PURE__*/React.createElement(Stack, {
    spacing: "s"
  }, /*#__PURE__*/React.createElement(SectionBlock, {
    section: section,
    hasPlaylistNumbering: hasPlaylistNumbering
  }), (hasSectionContent || showAsLocked) && /*#__PURE__*/React.createElement(Flex, {
    wrap: true,
    gap: "s"
  }, !showAsLocked && /*#__PURE__*/React.createElement(Button, _extends({
    size: "small",
    variant: "shaded",
    iconOnly: isVisible,
    iconRight: /*#__PURE__*/React.createElement(Icon, {
      name: "chevron",
      flip: isVisible ? 'y' : undefined,
      rotate: isVisible ? undefined : -90
    })
  }, buttonProps), t('actions_open'), /*#__PURE__*/React.createElement(VisuallyHidden, null, " ", section.name)), /*#__PURE__*/React.createElement(SectionContentInfo, {
    lockingInfoText: showAsLocked ? lockingInfoText : undefined,
    contentItemCount: hideItemCount ? undefined : contentItemCount,
    sectionDuration: sectionDuration
  })), hasReactChildren(children) && /*#__PURE__*/React.createElement("div", contentProps, children)));
}
export default Section;